import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useCallback, useRef, useState } from 'preact/hooks';
import { css } from 'react-emotion';
import { useGesture } from '@use-gesture/react';
import { withContext } from '../../common/context';
import { useMount } from '../../utils/useMount';
import { useEffectWhen } from '../../utils/useEffectWhen';
import { ZoomTrigger } from '../../typing/enums';
import { Events } from '../../utils/events';
import { ZoomWrapper, ZoomTriggerWrapper } from './Zoom.styled';
import { useZoomUtils } from './useZoomUtils';
import ZoomTip from './components/ZoomTip';
import ZoomImage from './components/ZoomImage';
import ZoomLens from './components/ZoomLens';
import MultiZoom from './components/MultiZoom';

const Zoom = props => {
  const wrapper = useRef(null);
  const [state, setState] = useState({
    left: 0,
    top: 0,
    isZoomed: false,
    transition: false,
    zoomStep: 0
  });

  const {
    isInline,
    isPopup,
    isMultiZoom,
    getZoomPointerCoordinates,
    getWrapper,
    getWrapperCoords,
    getLensCoords,
    getCurrentAsset
  } = useZoomUtils(props, state, wrapper);

  const setTransition = useCallback(() => {
    const duration = 150;
    setState(prev => ({ ...prev, transition: true }));

    setTimeout(() => {
      setState(prev => ({ ...prev, transition: false }));
    }, duration);
  }, []);

  const handleEsc = useCallback(
    e => {
      if (e.key === 'Escape' && !isPopup()) {
        e.preventDefault();
        zoomOut();
      }
    },
    // Ignoring to prevent a circular dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPopup]
  );

  const zoomIn = useCallback(
    (left = 0, top = 0, scale = 1) => {
      setState(prev => ({ ...prev, left, top, isZoomed: true, zoomStep: scale }));

      if (props.setZoom && !isMultiZoom()) {
        props.setZoom(props.index);
      }

      if (!isPopup()) {
        setTransition();
      }

      props.context.events(Events.ZOOM_IN, props.publicId);
      props.onZoomIn?.();
      document.body.addEventListener('keydown', handleEsc);
    },
    [props, isMultiZoom, isPopup, handleEsc, setTransition]
  );

  const zoomOut = useCallback(() => {
    setTransition();
    setState(prev => ({ ...prev, isZoomed: false, zoomStep: 0 }));

    props.onZoomOut?.();

    if (props.setZoom && !isPopup() && !isMultiZoom()) {
      props.setZoom(-1);
    }

    props.context.events(Events.ZOOM_OUT, props.publicId);
    document.body.removeEventListener('keydown', handleEsc);
  }, [setTransition, props, isPopup, isMultiZoom, handleEsc]);

  const setMultiZoom = useCallback(
    step => {
      setTransition();
      setState(prev => ({ ...prev, zoomStep: step, left: 0, top: 0 }));
    },
    [setTransition]
  );

  const bindGestures = useGesture(
    {
      onDrag: ({ event, initial: [ix, iy], delta: [dx, dy], tap }) => {
        if (tap) {
          // Handle click/tap
          if (props.trigger === ZoomTrigger.CLICK) {
            if (state.isZoomed) {
              zoomOut();
            } else {
              const { x, y } = getZoomPointerCoordinates(ix, iy);
              zoomIn(x, y);
            }
          }

          return;
        }

        if (state.isZoomed) {
          // Handle touch move
          setState(prev => ({
            ...prev,
            left: state.left - dx / props.level,
            top: state.top - dy / props.level
          }));
          // Prevent carousel swipe on zoom move
          event.stopPropagation();
          event.preventDefault();
        }
      },
      onMove: ({ xy: [ix, iy] }) => {
        // Mouse over
        if (state.isZoomed) {
          const { x, y } = getZoomPointerCoordinates(ix, iy);
          setState(prev => ({ ...prev, left: x, top: y }));
        }
      },
      onHover: ({ hovering, xy: [ix, iy] }) => {
        // Mouse enter/leave
        if (props.trigger === ZoomTrigger.HOVER) {
          if (hovering) {
            const { x, y } = getZoomPointerCoordinates(ix, iy);
            zoomIn(x, y);
          } else {
            zoomOut();
          }
        }
        if (state.isZoomed && !hovering) {
          const rect = getWrapper().getBoundingClientRect();
          const isOutside = ix < rect.left || ix > rect.right || iy < rect.top || iy > rect.bottom;
          if (isOutside) zoomOut();
        }
      },
      onPinch: ({ offset: [scale], delta: [delta] }) => {
        if (!isMultiZoom()) return;

        if (!state.isZoomed && delta > 0) {
          zoomIn(0, 0, delta);
        } else if (state.isZoomed && scale < 0.5 && delta < 0) {
          zoomOut();
        }

        setState(prev => ({ ...prev, zoomStep: prev.zoomStep + delta }));
      }
    },
    {
      drag: {
        eventOptions: { passive: false, capture: state.isZoomed },
        filterTaps: true
      },
      pinch: {
        scaleBounds: { min: 0, max: props.context.config.selectZoomPropsSteps() }
      }
    }
  );

  useMount(() => {
    if (props.initialZoom) {
      zoomIn();
    }

    return () => {
      if (state.isZoomed) {
        zoomOut();
      }
      document.body.removeEventListener('keydown', handleEsc);
    };
  });

  useEffectWhen(
    () => {
      if (props.zoomIn) {
        if (!state.isZoomed) {
          zoomIn();
        }
      } else {
        zoomOut();
      }
    },
    [props.zoomIn, zoomIn, zoomOut],
    (prev, current) => prev[0] !== current[0]
  );

  const setContainerStyles = useCallback(() => {
    if (props.viewerContainer) {
      const elem = document.querySelector(props.viewerContainer);
      if (elem) {
        const containerCls = css`
          overflow: hidden;
          width: ${props.width}px;
          height: ${props.height}px;
          ${!elem.style.position ? 'position: relative;' : ''};
        `;

        elem.classList.toggle(containerCls, state.isZoomed || state.transition);
      }
    }
  }, [props.viewerContainer, props.width, props.height, state.isZoomed, state.transition]);

  const wrapperCoords = getWrapperCoords();

  const zoomProps = {
    zoomed: state.isZoomed,
    url: props.url,
    width: props.width,
    height: props.height,
    level: Math.pow(props.level, state.zoomStep),
    top: wrapperCoords.y,
    left: wrapperCoords.x,
    lensLeft: state.left,
    lensTop: state.top,
    radius: props.context.config.selectZoomPropsViewerRadius(),
    zIndex: !(isInline() || isMultiZoom()) && props.context.config.selectZoomPropsViewerZIndex(),
    bgColor: props.context.config.selectThemePropsPrimary(),
    spacing: props.context.config.selectZoomPropsViewerOffset(),
    renderToContainer: props.viewerContainer,
    transition: state.transition,
    inPopup: props.inPopup
  };

  if (props.viewerContainer) {
    setContainerStyles();
  }

  const canZoom =
    isInline() || // Inline
    (!isInline() && !isPopup() && !props.inPopup) || // Flyout
    (isPopup() && !props.inPopup) || // Popup
    isMultiZoom(); // MultiZoom in popup

  return (
    <ZoomWrapper ref={wrapper} data-test="zoom-wrap">
      <ZoomTip />

      {(isInline() || isMultiZoom()) && <ZoomImage {...zoomProps} />}

      {!isInline() &&
        !isPopup() &&
        !isMultiZoom() &&
        createPortal(
          <ZoomImage {...zoomProps} />,
          (props.viewerContainer && document.querySelector(props.viewerContainer)) || document.body
        )}

      {!isPopup() && !isMultiZoom() && (
        <ZoomLens
          show={state.isZoomed && !isInline() && props.context.config.selectZoomPropsShowLens()}
          width={getLensCoords().width}
          height={getLensCoords().height}
          left={state.left}
          top={state.top}
        />
      )}

      <ZoomTriggerWrapper
        data-test="zoom-trigger"
        canZoom={canZoom}
        isZoomed={state.isZoomed}
        inPopup={props.inPopup}
        {...bindGestures()}
      />

      {isMultiZoom() && (
        <MultiZoom
          zoomIn={zoomIn}
          zoomOut={zoomOut}
          setMultiZoom={setMultiZoom}
          asset={getCurrentAsset()}
          level={props.level}
          currentStep={state.zoomStep}
          steps={props.context.config.selectZoomPropsSteps()}
        />
      )}
    </ZoomWrapper>
  );
};

export default withContext(Zoom);
